import { call, put, takeLatest } from "redux-saga/effects";
import axios from "axios";
import {
  fetchCustomerListRequest,
  fetchCustomerListSuccess,
  fetchCustomerListFailure,
  fetchInactiveCustomerListRequest,
  fetchInactiveCustomerListSuccess,
  fetchInactiveCustomerListFailure,
  updateCustomerStatusRequest,
  updateCustomerStatusSuccess,
  updateCustomerStatusFailure,
  getCustomerDetailsRequest,
  getCustomerDetailsSuccess,
  getCustomerDetailsFailure,
  sendGiftRequest,
  sendGiftSuccess,
  sendGiftFailure,
  extractCustomerDataSuccess,
  extractCustomerDataFailure,
  extractCustomerDataRequest,
  holdingsExportFailure,
  holdingsExportSuccess,
  holdingsExportRequest,
  createOrEditNomineeRequest,
  createOrEditNomineeSuccess,
  createOrEditNomineeFailure,
  exportInactiveCustomerRequest,
  exportInactiveCustomerSuccess,
  exportInactiveCustomerFailure,
  updateCustomerRequest,
  updateCustomerSuccess,
  updateCustomerFailure,
  monthEndReportRequest,
  monthEndReportSuccess,
  monthEndReportFailure,
} from "./CustomerSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import authConfig from "../../configs/auth";

// Fetch Active Customers
function* fetchCustomerSaga(action) {
  try {
    const requestBody = {
      start: 0,
      length: 1000,
      search: { value: "" },
      columns: [],
      order: [],
      ...(action.payload || {}),
    };

    const accessToken = window.localStorage.getItem(
      authConfig.storageTokenKeyName,
    );

    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/getCustomerList`,
        action.payload,
      ),
    );

    const result = response?.data.data;
    console.log("customers saga", result);

    yield put(fetchCustomerListSuccess({ data: result }));
  } catch (error) {
    console.error(" Error in fetchCustomerSaga:", error);
    yield put(
      fetchCustomerListFailure(error.response?.data?.message || error.message),
    );
  }
}

function* fetchInactiveCustomerSaga(action) {
  try {
    const {
      start = 0,
      limit = 10,
      search = "",
      startDate,
      endDate,
    } = action.payload || {};

    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/getInactiveCustomers?start=${start}&limit=${limit}&search=${search}`,
      { startDate, endDate },
    );

    const result = response.data;
    const inactiveCustomers = result.data?.inactiveCustomers || [];
    const total = result.recordsFiltered || 0;

    yield put(
      fetchInactiveCustomerListSuccess({ data: inactiveCustomers, total }),
    );
  } catch (error) {
    yield put(
      fetchInactiveCustomerListFailure(
        error.response?.data?.message || error.message,
      ),
    );
  }
}

function* getCustomerDetailsSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/getCustomerDetails/${action.payload.customerId}`,
      ),
    );
    yield put(getCustomerDetailsSuccess(response.data.data));
  } catch (error) {
    yield put(getCustomerDetailsFailure(error.message));
  }
}

function* updateCustomerStatusSaga(action) {
  try {
    const payload = action.payload;

    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/updateCustomerStatus`,
      payload,
    );

    const result = response.data?.data;
    yield put(
      updateCustomerStatusSuccess({
        customerId: payload.cid,
        newStatus: result?.newStatus || true,
      }),
    );
    yield put(fetchCustomerListRequest({ start: 0, limit: 10, search: "" }));
    toast.success("Customer status updated successfully!");
  } catch (error) {
    yield put(
      updateCustomerStatusFailure(
        error.response?.data?.message || error.message,
      ),
    );
    toast.error("Failed to update customer status.");
  }
}

function* sendGiftSaga(action) {
  try {
    const payload = action.payload;

    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/gift/send-customer-gift`,
      payload,
    );

    yield put(sendGiftSuccess(response.data));
    toast.success("Gift sent successfully!");
  } catch (error) {
    yield put(sendGiftFailure(error.response?.data?.message || error.message));
  }
}

function* extractCustomerDataSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/customer-export`,
      ),
    );

    yield put(extractCustomerDataSuccess(response.data?.message));
    toast.success(
      response.data?.message ?? "Customer export started successfully!",
    );
  } catch (error) {
    yield put(
      extractCustomerDataFailure(
        error.response?.data?.message || error.message,
      ),
    );
    toast.error("Failed to start customer export.");
  }
}

function* holdingsExportSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/extractallcustomerHoldingData`,
      ),
    );

    yield put(holdingsExportSuccess(response.data?.message));
    toast.success(
      response.data?.message ?? "Holdings export started successfully!",
    );
  } catch (error) {
    yield put(
      holdingsExportFailure(error.response?.data?.message || error.message),
    );
    toast.error("Failed to start holdings export.");
  }
}

function* createOrEditNomineeSaga(action) {
  try {
    const payload = action.payload;

    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/createOrEditNominee`,
      payload,
    );

    const { success, message } = response.data;

    if (!success) {
      yield put(createOrEditNomineeFailure(message));
      toast.warning(message || "Something went wrong");
      return;
    }

    // success === true
    yield put(
      createOrEditNomineeSuccess(message || "Nominee saved successfully!"),
    );
    yield put(fetchCustomerListRequest({ start: 0, limit: 10, search: "" }));
    toast.success(message || "Nominee saved successfully!");
  } catch (error) {
    yield put(
      createOrEditNomineeFailure(
        error.response?.data?.message || error.message,
      ),
    );
    toast.error(error.response?.data?.message || "Failed to save nominee");
  }
}

function* exportInactiveCustomersSaga(action) {
  try {
    const response = yield call(() =>
      axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/exportInactiveCustomers`,
        action.payload,
      ),
    );

    if (!response?.data?.success) {
      toast.error(response.data.message || "Export failed!");
      yield put(exportInactiveCustomerFailure(response.data.message));
      return;
    }

    yield put(exportInactiveCustomerSuccess(response.data));
    toast.success(response.data.message || "Export started!");
  } catch (error) {
    yield put(exportInactiveCustomerFailure(error.message));
    toast.error("Export failed!");
  }
}

function* updateCustomer(action) {
  try {
    const response = yield call(
      axios.post,
      `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/updateCustomer`,
      action.payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    if (response.data.success) {
      yield put(updateCustomerSuccess(response.data));
      yield put(fetchCustomerListRequest({ start: 0, limit: 10, search: "" }));
      toast.success(response.data.message || "Customer updated successfully");
    } else {
      yield put(
        updateCustomerFailure(
          response.data.message || "Failed to update customer",
        ),
      );
      toast.error(response.data.message || "Failed to update customer");
    }
  } catch (error) {
    yield put(
      updateCustomerFailure(error.response?.data?.message || error.message),
    );
  }
}

function* monthEndReportSaga(action) {
  try {
    const response = yield call(() =>
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer/extractMonthEndReport`,
        {params: action.payload},
      ),
    );

    yield put(monthEndReportSuccess(response.data?.message));
    toast.success(
      response.data?.message ?? "Month end report generated successfully!",
    );
  } catch (error) {
    yield put(
      monthEndReportFailure(error.response?.data?.message || error.message),
    );
    toast.error("Failed to generate month end report.");
  }
}


// Root saga
export function* CustomerSaga() {
  yield takeLatest(fetchCustomerListRequest.type, fetchCustomerSaga);
  yield takeLatest(
    fetchInactiveCustomerListRequest.type,
    fetchInactiveCustomerSaga,
  );
  yield takeLatest(updateCustomerStatusRequest.type, updateCustomerStatusSaga);
  yield takeLatest(getCustomerDetailsRequest.type, getCustomerDetailsSaga);
  yield takeLatest(sendGiftRequest.type, sendGiftSaga);
  yield takeLatest(extractCustomerDataRequest.type, extractCustomerDataSaga);
  yield takeLatest(holdingsExportRequest.type, holdingsExportSaga);
  yield takeLatest(createOrEditNomineeRequest.type, createOrEditNomineeSaga);
  yield takeLatest(
    exportInactiveCustomerRequest.type,
    exportInactiveCustomersSaga,
  );
  yield takeLatest(updateCustomerRequest.type, updateCustomer);
  yield takeLatest(monthEndReportRequest.type, monthEndReportSaga);
}
